import {Component, CUSTOM_ELEMENTS_SCHEMA, Input, OnInit} from '@angular/core';

import {FormsModule} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {HubTranslationEntry, HubTranslationImportReport, HubTranslationsResponse} from '../../model/hub-session';
import {HubApiService} from '../../services/hub-api.service';
import '@web.awesome.me/webawesome-pro/dist/components/icon/icon.js';
@Component({
  selector: 'hub-translations',
  imports: [FormsModule],
  templateUrl: './translations.component.html',
  styleUrl: './translations.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class TranslationsComponent implements OnInit {
  private static readonly BULK_CALL_SPACING_MS = 1000;
  private static readonly BULK_BACKOFF_START_MS = 2000;
  private static readonly BULK_BACKOFF_MAX_MS = 30000;
  private static readonly BULK_ATTEMPTS_PER_ITEM = 4;
  private static readonly BULK_ABORT_AFTER_FAILED_ITEMS = 3;

  @Input() embedded = false;
  @Input() embeddedProcessDefinitionId?: number;

  data: HubTranslationsResponse | null = null;
  openedLabel = '';
  newLabel = '';
  createModalOpen = false;
  importModalOpen = false;
  importReport: HubTranslationImportReport | null = null;
  importFileContent = '';
  importing = false;
  translating: Record<string, boolean> = {};
  bulkModalOpen = false;
  bulkRunning = false;
  bulkFinished = false;
  bulkCancelRequested = false;
  bulkAborted = false;
  bulkTotal = 0;
  bulkDone = 0;
  bulkFailed: string[] = [];
  bulkCurrentLabel = '';
  bulkCurrentLanguage = '';
  bulkRetryInSeconds = 0;
  processDefinitionId?: number;
  scope: 'global' | 'process' = 'global';
  loading = true;
  error = '';
  success = '';

  constructor(private hubApi: HubApiService, private route: ActivatedRoute) {
  }

  ngOnInit(): void {
    const id = this.embeddedProcessDefinitionId ?? this.route.snapshot.paramMap.get('processDefinitionId');
    if (id !== null && id !== undefined) {
      this.scope = 'process';
      this.processDefinitionId = Number(id);
    }
    this.reload();
  }

  save(entry: HubTranslationEntry): void {
    this.clearMessages();
    this.hubApi.saveTranslation(this.scope, entry.label, entry.format, entry.translations, this.processDefinitionId).subscribe({
      next: (data) => {
        this.data = data;
        this.openedLabel = entry.label;
        this.success = this.t('HubTranslationSavedMessage');
      },
      error: (error) => this.error = error.error?.message || this.t('HubTranslationSaveFailedMessage')
    });
  }

  untranslatedCount(): number {
    if (!this.data) return 0;
    let count = 0;
    for (const entry of this.data.translations) {
      for (const language of this.data.languages) {
        if (!String(entry.translations[language] ?? '').trim()) count++;
      }
    }
    return count;
  }

  startBulkTranslate(): void {
    if (!this.data || this.bulkRunning) return;
    const queue: {label: string, language: string}[] = [];
    for (const entry of this.data.translations) {
      for (const language of this.data.languages) {
        if (!String(entry.translations[language] ?? '').trim()) {
          queue.push({label: entry.label, language});
        }
      }
    }
    if (queue.length === 0) return;
    this.clearMessages();
    this.bulkModalOpen = true;
    this.bulkRunning = true;
    this.bulkFinished = false;
    this.bulkCancelRequested = false;
    this.bulkAborted = false;
    this.bulkTotal = queue.length;
    this.bulkDone = 0;
    this.bulkFailed = [];
    this.bulkRetryInSeconds = 0;
    this.runBulkTranslate(queue);
  }

  cancelBulkTranslate(): void {
    this.bulkCancelRequested = true;
  }

  closeBulkModal(): void {
    if (this.bulkRunning) return;
    this.bulkModalOpen = false;
  }

  bulkProgressPercent(): number {
    return this.bulkTotal ? Math.round((this.bulkDone / this.bulkTotal) * 100) : 0;
  }

  bulkProgressText(): string {
    return this.t('HubBulkTranslateProgress')
      .replace('{done}', String(this.bulkDone))
      .replace('{total}', String(this.bulkTotal));
  }

  bulkCurrentText(): string {
    return this.t('HubBulkTranslateCurrent')
      .replace('{label}', this.bulkCurrentLabel)
      .replace('{language}', this.bulkCurrentLanguage);
  }

  bulkRetryText(): string {
    return this.t('HubBulkTranslateRetrying').replace('{seconds}', String(this.bulkRetryInSeconds));
  }

  private async runBulkTranslate(queue: {label: string, language: string}[]): Promise<void> {
    let failedItemsInARow = 0;
    for (const item of queue) {
      if (this.bulkCancelRequested) break;
      // The item may have been filled in the meantime (each response carries
      // the full data set, e.g. after an LLM call that also touched it).
      if (!this.isStillUntranslated(item.label, item.language)) {
        this.bulkDone++;
        continue;
      }
      this.bulkCurrentLabel = item.label;
      this.bulkCurrentLanguage = this.data?.languageNames?.[item.language] || item.language;
      const ok = await this.bulkTranslateItem(item);
      if (this.bulkCancelRequested) break;
      if (ok) {
        failedItemsInARow = 0;
        this.bulkDone++;
      } else {
        this.bulkFailed.push(`${item.label} (${item.language})`);
        failedItemsInARow++;
        if (failedItemsInARow >= TranslationsComponent.BULK_ABORT_AFTER_FAILED_ITEMS) {
          this.bulkAborted = true;
          break;
        }
      }
      await this.bulkSleep(TranslationsComponent.BULK_CALL_SPACING_MS);
    }
    this.bulkRunning = false;
    this.bulkFinished = true;
    this.bulkCurrentLabel = '';
    this.bulkCurrentLanguage = '';
    this.bulkRetryInSeconds = 0;
  }

  private async bulkTranslateItem(item: {label: string, language: string}): Promise<boolean> {
    let backoff = TranslationsComponent.BULK_BACKOFF_START_MS;
    for (let attempt = 1; attempt <= TranslationsComponent.BULK_ATTEMPTS_PER_ITEM; attempt++) {
      if (this.bulkCancelRequested) return false;
      try {
        this.data = await firstValueFrom(
          this.hubApi.autoTranslate(this.scope, item.label, item.language, this.processDefinitionId)
        );
        return true;
      } catch {
        if (attempt === TranslationsComponent.BULK_ATTEMPTS_PER_ITEM) return false;
        this.bulkRetryInSeconds = Math.round(backoff / 1000);
        await this.bulkSleep(backoff);
        this.bulkRetryInSeconds = 0;
        backoff = Math.min(backoff * 2, TranslationsComponent.BULK_BACKOFF_MAX_MS);
      }
    }
    return false;
  }

  private isStillUntranslated(label: string, language: string): boolean {
    const entry = this.data?.translations.find((candidate) => candidate.label === label);
    return Boolean(entry) && !String(entry!.translations[language] ?? '').trim();
  }

  // Sleeps in small steps so a cancel takes effect quickly even during a long
  // backoff wait.
  private async bulkSleep(ms: number): Promise<void> {
    const step = 200;
    for (let waited = 0; waited < ms && !this.bulkCancelRequested; waited += step) {
      await new Promise((resolve) => setTimeout(resolve, step));
    }
  }

  autoTranslate(entry: HubTranslationEntry, language: string): void {
    const key = this.translationKey(entry.label, language);
    this.clearMessages();
    this.translating[key] = true;
    this.hubApi.autoTranslate(this.scope, entry.label, language, this.processDefinitionId).subscribe({
      next: (data) => {
        this.data = data;
        this.openedLabel = entry.label;
        this.success = this.t('HubTranslationAutoTranslatedMessage');
        this.translating[key] = false;
      },
      error: (error) => {
        this.error = error.error?.message || this.t('HubTranslationAutoTranslateFailedMessage');
        this.translating[key] = false;
      }
    });
  }

  createGlobalLabel(): void {
    const label = this.newLabel.trim();
    if (!label) return;
    this.clearMessages();
    this.hubApi.createGlobalLabel(label).subscribe({
      next: (data) => {
        this.data = data;
        this.openedLabel = label;
        this.newLabel = '';
        this.createModalOpen = false;
        this.success = this.t('HubTranslationLabelCreatedMessage');
      },
      error: (error) => this.error = error.error?.message || this.t('HubTranslationLabelCreateFailedMessage')
    });
  }

  deleteGlobalLabel(entry: HubTranslationEntry): void {
    if (!confirm(this.t('HubDeleteLabelConfirmation').replace('{label}', entry.label))) return;
    this.clearMessages();
    this.hubApi.deleteGlobalLabel(entry.label).subscribe({
      next: (data) => {
        this.data = data;
        this.success = this.t('HubTranslationLabelDeletedMessage');
      },
      error: (error) => this.error = error.error?.message || this.t('HubTranslationLabelDeleteFailedMessage')
    });
  }

  exportTranslations(): void {
    const link = document.createElement('a');
    link.href = this.hubApi.translationsExportUrl(this.scope, this.processDefinitionId);
    link.download = '';
    link.click();
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      this.importFileContent = String(reader.result ?? '');
      this.requestImport(true);
    };
    reader.onerror = () => {
      this.error = this.t('HubTranslationImportFailedMessage');
    };
    reader.readAsText(file);
  }

  applyImport(): void {
    this.requestImport(false);
  }

  closeImportModal(): void {
    this.importModalOpen = false;
    this.importReport = null;
    this.importFileContent = '';
  }

  importCountText(key: string, count: number): string {
    return this.t(key).replace('{count}', String(count));
  }

  private requestImport(dryRun: boolean): void {
    this.clearMessages();
    this.importing = true;
    this.hubApi.importTranslations(this.scope, this.importFileContent, dryRun, this.processDefinitionId).subscribe({
      next: (data) => {
        this.importing = false;
        this.importReport = data.report;
        if (dryRun) {
          this.importModalOpen = true;
        } else {
          this.data = data;
          this.closeImportModal();
          this.success = this.t('HubTranslationImportedMessage');
        }
      },
      error: (error) => {
        this.importing = false;
        this.closeImportModal();
        this.error = error.error?.message || this.t('HubTranslationImportFailedMessage');
      }
    });
  }

  toggle(label: string): void {
    this.openedLabel = this.openedLabel === label ? '' : label;
  }

  t(key: string): string {
    return this.hubApi.translate(key) || key;
  }

  canAutoTranslate(entry: HubTranslationEntry, language: string): boolean {
    return !this.isTranslating(entry, language);
  }

  statusLabel(entry: HubTranslationEntry): string {
    const labels = [];
    if (entry.allTranslated) labels.push(this.t('HubComplete'));
    if (entry.format === 'markdown') labels.push('Markdown');
    if (entry.system) labels.push(this.t('HubSystemLabel'));
    return labels.join(', ');
  }

  isTranslating(entry: HubTranslationEntry, language: string): boolean {
    return Boolean(this.translating[this.translationKey(entry.label, language)]);
  }

  private translationKey(label: string, language: string): string {
    return `${label}:${language}`;
  }

  private reload(): void {
    this.hubApi.loadTranslations(this.scope, this.processDefinitionId).subscribe({
      next: (data) => {
        this.data = data;
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || this.t('HubTranslationsLoadFailedMessage');
        this.loading = false;
      }
    });
  }

  private clearMessages(): void {
    this.error = '';
    this.success = '';
  }
}
